import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaSync,
  FaPalette,
  FaCog,
  FaFire,
  FaCoffee,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
  useReadDeviceSettingsV2Query,
  useWriteDeviceSettingsV2Mutation,
  devicesSettingsV2Api,
} from "../../../../store/storageRoles/devicesSettingsV2Api";
import useAuth from "../../../../Hooks/useAuth";
import EmptyState from "../../../shared/EmptyState";
import FilterTabs from "../../../shared/FilterTabs";
import SearchInput from "../../../shared/SearchInput";
import AppearanceSection from "./AppearanceSection";
import ProfileSettingsSection from "./ProfileSettingsSection";
import GeneralSection from "./GeneralSection";
import CustomPotSection from "./CustomPotSection";
import ChangesPanel from "./ChangesPanel";
import SettingsSkeleton from "./SettingsSkeleton";
import {
  fold,
  categoryNamesForKey,
  categoryNamesForPath,
  anyNameMatches,
} from "./settingsSearchIndex";
import useDirtyChanges from "./hooks/useDirtyChanges";
import valuesEqual from "./hooks/valuesEqual";
import { validateCurve } from "./autoMixerCurve";
import { pushTimedAlert, settingsSaveAlert } from "../../../../utils/settingsSaveAlert";

// Bölüm tanımı: id + label + ikon + renk + alan sayısı için path
// Bölüm görünürlüğü yetkiye bakmaz: sunucu okuma süzgecini uyguladığı için
// (device-setting-scope) seçilmemiş anahtar zaten gelmez; alanı kalmayan
// bölümün sekmesi de gösterilmez. Bölüm bazlı yetkiler kaldırıldı — erişim
// yalnızca rol şablonundaki anahtar seçiminden gelir.
// Sekme sayısı bilinçli olarak azaltıldı (2026-09-01): Şifre Ayarları ve Ödeme
// "Sistem"in, Tarih ve Saat ise "Görüntü ve Saat"in alt grubu olarak render
// edilir. Yazma grupları AYNI kaldı — birleşme yalnızca sunum katmanındadır.
// Etiketler makinedeki menü adlarıyla aynı tutulur (kullanıcı kuralı).
const SECTION_DEFS = [
  { id: "settings", label: "Sistem", icon: FaCog, color: "blue" },
  { id: "appearance", label: "Görüntü ve Saat", icon: FaPalette, color: "indigo" },
  { id: "general", label: "Genel Ayarlar", icon: FaFire, color: "emerald" },
  { id: "pots", label: "Göz Ayarları", icon: FaCoffee, color: "amber" },
];

const countFields = (obj) => {
  if (!obj || typeof obj !== "object") return 0;
  let n = 0;
  for (const v of Object.values(obj)) {
    if (v && typeof v === "object" && "type" in v && "label" in v) n++;
    else if (v && typeof v === "object") n += countFields(v);
  }
  return n;
};

// Bir change hangi UI section'a ait — FilterTabs id'sine map'ler
const sectionOfChange = (change) => {
  if (!change || !change.group) return null;
  if (change.group === "profile.security") return "settings";
  if (change.group === "profile.appearance") return "appearance";
  if (change.group === "profile.settings") return "settings";
  if (change.group === "profile.timeDate") return "appearance";
  if (change.group.startsWith("general.")) return "general";
  if (change.group === "custom") return "pots";
  if (change.group === "payment") return "settings";
  return null;
};

// Setting metadata'sini (label, value, type...) bul.
const findSettingMeta = (data, change) => {
  if (!data || !change) return null;
  try {
    if (
      change.group.startsWith("profile.") ||
      change.group.startsWith("general.")
    ) {
      const [root, sub] = change.group.split(".");
      return data?.[root]?.[sub]?.[change.key] || null;
    } else if (change.group === "payment") {
      return data?.payment?.[change.key] || null;
    } else if (change.group === "custom") {
      const pots = data.custom?.pots || [];
      const pot =
        pots.find(
          (p) =>
            p.index === change.potIndex &&
            (!change.type || p.type === change.type),
        ) || pots.find((p) => p.index === change.potIndex);
      if (!pot) return null;
      // Once nested sub-object dene, yoksa flat (pot root)
      const sub = pot[change.subgroup];
      const isNestedSub =
        sub &&
        typeof sub === "object" &&
        !Array.isArray(sub) &&
        !("type" in sub);
      const container = isNestedSub ? sub : pot;
      if (change.cupId) {
        const cups = Array.isArray(container.cups) ? container.cups : [];
        const cup = cups.find((c) => c.id === change.cupId);
        if (!cup) return null;
        if (change.mode) return cup.modes?.[change.mode]?.[change.key] || null;
        return cup[change.key] || null;
      }
      return container[change.key] || null;
    }
  } catch (_) {}
  return null;
};

// Bir change ilgili setting metadata'sina gore valid mi?
// - Input: regex ile uyumlu mu
// - Number: lowerBound <= value <= upperBound mu
// - Diger tipler: simdilik gecerli kabul edilir
const isChangeValid = (data, change) => {
  const meta = findSettingMeta(data, change);
  if (!meta) return true; // Metadata bulunamadiysa engelleme
  if (meta.type === "Number") {
    if (typeof change.value !== "number" || Number.isNaN(change.value))
      return false;
    if (typeof meta.lowerBound === "number" && change.value < meta.lowerBound)
      return false;
    const upper = meta.upperBound ?? meta.upperBounde;
    if (typeof upper === "number" && change.value > upper) return false;
    return true;
  }
  if (meta.type === "AutoMixerCurve") {
    // null = varsayılan eğri; aksi halde tabletteki sınırlar
    return change.value === null || !!validateCurve(change.value, meta.bounds);
  }
  if (meta.type === "Input") {
    if (typeof change.value !== "string") return false;
    if (!meta.regex) return true;
    try {
      return new RegExp(meta.regex).test(change.value);
    } catch (_) {
      return true;
    }
  }
  if (meta.type === "TeaFilterCups") {
    if (!Array.isArray(change.value)) return false;
    const cups = change.value;
    const basicValid = cups.every(
      (cup) =>
        cup &&
        typeof cup.id === "string" &&
        typeof cup.name === "string" &&
        cup.name.trim().length > 0 &&
        [
          "filledWeight",
          "emptyWeight",
          "lowerTolerance",
          "upperTolerance",
        ].every((k) => typeof cup[k] === "number" && cup[k] >= 0),
    );
    if (!basicValid) return false;
    // Tolerans zinciri: lowerTolerance <= emptyWeight <= upperTolerance
    for (const cup of cups) {
      if (cup.lowerTolerance > cup.emptyWeight) return false;
      if (cup.emptyWeight > cup.upperTolerance) return false;
    }
    // Bardaklar arasi cakisma kontrolu — [lowerTol, upperTol] araliklari
    // kesisemez ve diger bardagin emptyWeight'i bu bardagin araligina
    // duseememeli.
    for (let i = 0; i < cups.length; i++) {
      for (let j = i + 1; j < cups.length; j++) {
        const a = cups[i];
        const b = cups[j];
        const rangesOverlap =
          a.lowerTolerance <= b.upperTolerance &&
          b.lowerTolerance <= a.upperTolerance;
        if (rangesOverlap) return false;
        if (
          b.emptyWeight >= a.lowerTolerance &&
          b.emptyWeight <= a.upperTolerance
        )
          return false;
        if (
          a.emptyWeight >= b.lowerTolerance &&
          a.emptyWeight <= b.upperTolerance
        )
          return false;
      }
    }
    return true;
  }
  return true;
};

// Pot type ("TFC"/"TC") tespiti — cihaz pot.type gondermezse alan adlarindan tahmin
const TFC_KEY_HINTS = new Set([
  "potCalibrationTime",
  "massScale",
  "autoBrewMassInTheAir",
  "autoWaterMassInTheAir",
]);
const TC_FLAT_KEY_HINTS = new Set([
  "cleaningWaterLevel",
  "cupDisabled",
  "cupAlready",
  "defaultPowerMode",
  "defaultCup",
]);

const detectPotType = (pot) => {
  if (pot?.type === "TFC" || pot?.type === "TC") return pot.type;
  if (Array.isArray(pot?.cups)) return "TC";
  for (const k of Object.keys(pot || {})) {
    if (TFC_KEY_HINTS.has(k)) return "TFC";
    if (TC_FLAT_KEY_HINTS.has(k)) return "TC";
  }
  return "TFC";
};

// Pot listesini type/displayIndex ile zenginlestir (cihaz gondermese bile)
const decoratePots = (data) => {
  if (!data?.custom?.pots) return data;
  let tfcN = 0;
  let tcN = 0;
  const decorated = data.custom.pots.map((pot) => {
    const type = detectPotType(pot);
    const displayIndex =
      typeof pot.displayIndex === "number"
        ? pot.displayIndex
        : type === "TFC"
          ? ++tfcN
          : ++tcN;
    return { ...pot, type, displayIndex };
  });
  return {
    ...data,
    custom: {
      ...data.custom,
      pots: decorated,
    },
  };
};

// valuesEqual ortak util'den (./hooks/valuesEqual) import edildi —
// primitive array'ler icin sira-bagimsiz karsilastirma yapar.

// Pending change listesini data structure'una overlay eder — UI butonlara
// basildigi anda yeni degeri gosterir, kaydeti beklemez.
const applyChangesToData = (data, changes) => {
  if (!data || !Array.isArray(changes) || changes.length === 0) return data;
  // Derin kopya — orijinali mutasyona ugratmayalim
  const next = JSON.parse(JSON.stringify(data));
  for (const ch of changes) {
    if (!ch || !ch.group) continue;
    if (ch.group.startsWith("profile.") || ch.group.startsWith("general.")) {
      const [root, sub] = ch.group.split(".");
      const target = next?.[root]?.[sub]?.[ch.key];
      if (target && typeof target === "object") {
        target.value = ch.value;
      }
    } else if (ch.group === "custom") {
      // Pot alanlari flat — pot.subgroup nested object DEGIL. subgroup
      // sadece write semantigi icin metadata. cups[] ve flat alanlar
      // pot'un kendi root'unda.
      // Eski pot'larda hala teaFilterCoffee/turkishCoffee sub-object'i
      // varsa onu da destekleriz (backward compat).
      const pots = next.custom?.pots || [];
      const pot =
        pots.find(
          (p) => p.index === ch.potIndex && (!ch.type || p.type === ch.type),
        ) || pots.find((p) => p.index === ch.potIndex);
      if (!pot) continue;
      // Once nested sub-object dene (eski sema), yoksa flat
      const sub = pot[ch.subgroup];
      const isNestedSub =
        sub &&
        typeof sub === "object" &&
        !Array.isArray(sub) &&
        !("type" in sub);
      const container = isNestedSub ? sub : pot;
      if (ch.cupId) {
        const cups = Array.isArray(container.cups) ? container.cups : [];
        const cup = cups.find((c) => c.id === ch.cupId);
        if (!cup) continue;
        if (ch.mode) {
          const modeData = cup.modes?.[ch.mode];
          if (modeData && modeData[ch.key]) modeData[ch.key].value = ch.value;
        } else if (cup[ch.key]) {
          cup[ch.key].value = ch.value;
        }
      } else if (container[ch.key] && typeof container[ch.key] === "object") {
        container[ch.key].value = ch.value;
      }
    } else if (ch.group === "payment") {
      const target = next.payment?.[ch.key];
      if (target && typeof target === "object") {
        target.value = ch.value;
      }
    }
  }
  return next;
};

// Arama: alan etiketi VEYA alanın kategorisi eşleşirse alan sonuçta kalır.
// Kategori = bölüm/alt grup adı (veri yolundan) + konu başlığı (anahtardan);
// sözlük settingsSearchIndex.js'te, ekrandaki adlarla aynı.
// Bir kategori adı eşleşince o dalın TAMAMI gösterilir ("kazan" → Kazan
// Ayarı'nın bütün alanları).
const filterByLabel = (data, term) => {
  if (!term) return data;
  const q = fold(term);
  const visit = (obj, path, key, inherited) => {
    if (!obj || typeof obj !== "object") return obj;
    if ("type" in obj && "label" in obj) {
      if (inherited) return obj;
      if (fold(obj.label).includes(q)) return obj;
      // Konu başlığı eşleşmesi yalnızca konu tanımı OLAN dallarda geçerli —
      // aksi halde aynı ada sahip göz-içi anahtarlar ("cleaning" gibi)
      // yanlışlıkla eşleşirdi.
      const hasTopics =
        path.startsWith("general.") || path.startsWith("profile.settings.");
      if (hasTopics && anyNameMatches(categoryNamesForKey(key), q)) return obj;
      return null;
    }
    // Diziler (custom.pots) dizi olarak korunur — obje'ye çevrilirse
    // CustomPotSection'ın Array.isArray kontrolü düşer ve göz ayarları
    // aramada hiç görünmez.
    const isArr = Array.isArray(obj);
    const out = isArr ? [] : {};
    let any = false;
    for (const k of Object.keys(obj)) {
      // Dizi indeksleri yola girmez; yol kategori sözlüğüyle eşleşmeli.
      const childPath = isArr ? path : path ? `${path}.${k}` : k;
      const childInherited =
        inherited || anyNameMatches(categoryNamesForPath(childPath), q);
      const v = visit(obj[k], childPath, k, childInherited);
      if (v !== null && v !== undefined) {
        if (
          typeof v === "object" &&
          !("type" in v) &&
          Object.keys(v).length === 0
        )
          continue;
        if (isArr) out.push(v);
        else out[k] = v;
        any = true;
      }
    }
    return any ? out : null;
  };
  return visit(data, "", null, false);
};

export default function SettingsTabV2({
  selectedDevice,
  setAlerts,
  isDrawer = false,
}) {
  const { auth } = useAuth();
  const id = selectedDevice?._id;
  const accessToken = auth?.accessToken;
  const dispatch = useDispatch();
  // Args memoize — her render'da yeni obje literal'i RTK Query'nin cache
  // key olusturucusunu yormasin diye sabit reference tutulur.
  const queryArgs = useMemo(
    () => ({ id, token: accessToken }),
    [id, accessToken],
  );
  // skip kosulu: hem id hem token hazir olmadan istek atilmaz — bu sayede
  // ilk render'da token undefined iken bos istek + token gelince refetch
  // seklinde duplicate cagri olusmaz.
  const { data, isLoading, isFetching, error, refetch } =
    useReadDeviceSettingsV2Query(queryArgs, {
      skip: !id || !accessToken,
    });
  const [writeMutation, { isLoading: isWriting }] =
    useWriteDeviceSettingsV2Mutation();
  const { changes, upsert, remove, clear, count } = useDirtyChanges();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  // Görünür bölümler YETKİDEN DEĞİL, GELEN VERİDEN türetilir: sunucu okuma
  // süzgecini zaten uyguluyor (device-setting-scope), izinsiz anahtar hiç
  // gelmiyor. Yetkiye bakmak yanlıştı — kullanıcı yalnızca anahtar seçtiğinde
  // (bölüm yetkisi vermeden) bölüm sekmesi açılmıyordu.
  const visibleSections = useMemo(() => {
    const d = data;
    if (!d) return SECTION_DEFS;
    const potFieldCount = Array.isArray(d.custom?.pots)
      ? d.custom.pots.reduce((acc, p) => acc + countFields(p), 0)
      : 0;
    const countBySection = {
      appearance:
        countFields(d.profile?.appearance) + countFields(d.profile?.timeDate),
      settings:
        countFields(d.profile?.settings) +
        countFields(d.profile?.security) +
        countFields(d.payment),
      general: countFields(d.general),
      pots: potFieldCount,
    };
    return SECTION_DEFS.filter((sec) => countBySection[sec.id] > 0);
  }, [data]);
  const [activeSection, setActiveSection] = useState("settings");

  // Veri gelince aktif bölüm görünmez kalabilir — ilk görünür bölüme kaydır.
  useEffect(() => {
    if (visibleSections.length === 0) return;
    if (!visibleSections.some((sec) => sec.id === activeSection)) {
      setActiveSection(visibleSections[0].id);
    }
  }, [visibleSections, activeSection]);
  // Manuel yenile durumunda skeleton goster — handleRefresh true yapar,
  // yeni data geldiginde (isFetching false ve data degisti) false'a doner.
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  // Kaydet sırasında skeleton goster — handleSave true yapar, write + sonrasi
  // refetch tamamlaninca (isFetching true→false) false'a doner.
  const [isSaving, setIsSaving] = useState(false);

  // Original snapshot — dirty hesabi icin sabit kalir, refetch'te yenilenir.
  // Pots decorate edilmis sekilde tutulur (type/displayIndex) — ChangesPanel
  // ve diger consumer'lar tutarli yapi gorur.
  const originalRef = useRef(null);
  // Absolute header'in yuksekligini olcup icerik alanina paddingTop olarak
  // ver — boylece header sayfa tepesinde sabit dururken icerik altinda
  // gizlenmez. ResizeObserver header genisleyince/kuculuyse paddingTop'u
  // canli gunceller.
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
    // İlk mount'ta skeleton/error gösterildiği için header henüz DOM'da yok
    // (headerRef null). Skeleton → içerik geçişinde (isLoading/data/error/
    // isManualRefresh/isSaving değişince) effect yeniden koşup header'ı ölçsün;
    // aksi halde headerH 0 kalır, paddingTop verilmez ve ayar listesi header'ın
    // altında gizlenir. (Kaydet skeleton'ı kalkınca da yeniden ölçülmeli.)
  }, [isLoading, isFetching, isManualRefresh, isSaving, data, error]);
  useEffect(() => {
    if (data && !originalRef.current) {
      originalRef.current = decoratePots(data);
    }
  }, [data]);

  const handleChange = (change) => {
    // No-op tespiti: yeni deger orijinal degerle ayni mi?
    const orig = originalRef.current || data;
    const origMeta = findSettingMeta(orig, change);
    const origValue = origMeta?.value;
    if (valuesEqual(origValue, change.value)) {
      // Geri donus — pending degisiklik listesinden cikar
      remove(change);
      return;
    }
    upsert(change);
  };
  const handleRefresh = async () => {
    originalRef.current = null;
    clear();
    setResults(null);
    setIsManualRefresh(true);
    refetch();
  };

  // Manuel refresh sonu — isFetching true→false transition'inda sifirla.
  // Direkt !isFetching kontrolu olsaydi, refetch henuz baslamadan effect
  // tetiklenip flag'i sifirlardi (yaris durumu).
  const wasFetchingRef = useRef(false);
  useEffect(() => {
    if (isManualRefresh && wasFetchingRef.current && !isFetching) {
      setIsManualRefresh(false);
    }
    // Kaydet sonrası refetch tamamlaninca skeleton'i kapat.
    if (isSaving && wasFetchingRef.current && !isFetching) {
      setIsSaving(false);
    }
    wasFetchingRef.current = isFetching;
  }, [isManualRefresh, isSaving, isFetching]);
  const handleSave = async (toSend) => {
    setIsSaving(true);
    try {
      const resp = await writeMutation({
        id,
        changes: toSend,
        token: auth?.accessToken,
      }).unwrap();
      // Cihazın KABUL ettiği değerleri optimistik olarak read cache'ine yaz;
      // reddedilenleri UYGULAMA (eski değer korunur — red bilgisi zaten sonuç
      // panelinde gösteriliyor). Not: write mutation'ından invalidatesTags
      // kaldırıldı çünkü kaydetten hemen sonra tetiklenen otomatik refetch,
      // cihazın henüz senkronlanmamış (eski/gecikmeli) durumunu okuyup bu
      // optimistik değeri eziyordu — kullanıcı sayfayı yenilemeden yeni değeri
      // göremiyordu (ör. hata kodu 000 kaydedilir, input eski değerde kalırdı).
      const payload = resp?.data ?? resp;
      const resultItems = Array.isArray(payload?.results) ? payload.results : [];
      const sameChangeId = (a, b) =>
        a.group === b.group &&
        a.key === b.key &&
        a.potIndex === b.potIndex &&
        a.subgroup === b.subgroup &&
        a.cupId === b.cupId &&
        a.mode === b.mode;
      const acceptedChanges = resultItems.length
        ? toSend.filter((ch) => {
            const r = resultItems.find((it) => sameChangeId(it, ch));
            // Eşleşme yoksa (eski server şeması) kabul varsay
            return r ? r.success !== false : true;
          })
        : toSend;
      dispatch(
        devicesSettingsV2Api.util.updateQueryData(
          "readDeviceSettingsV2",
          queryArgs,
          (draft) => applyChangesToData(draft, acceptedChanges),
        ),
      );
      // transformResponse yanıtı zaten açıyor — resp.data hep undefined'dı,
      // sonuç paneli kayboluyor ve kullanıcı hiçbir şey görmüyordu (#258).
      setResults(payload);
      const saveAlert = settingsSaveAlert(payload);
      pushTimedAlert(setAlerts, saveAlert.message, saveAlert.severity);
      clear();
      // original'i yeni (patch'li) data'dan kursun diye sıfırla
      originalRef.current = null;
      // "Varsayılana Dön" kabul edildiyse yeni bardak listesi makinede
      // oluştu — optimistik overlay bunu bilemez, taze READ çek.
      if (acceptedChanges.some((ch) => ch.key === "resetCupsToDefault")) {
        refetch();
      }
      // invalidatesTags kaldırıldığı için refetch tetiklenmez — skeleton'i
      // burada elle kapat (aksi halde isFetching döngüsü olmadan takılırdı).
      setIsSaving(false);
    } catch (err) {
      // Hata: refetch tetiklenmez (invalidatesTags sadece basarida), bu yuzden
      // isSaving'i burada elle kapat ki skeleton'da takilmasin.
      setIsSaving(false);
      pushTimedAlert(setAlerts, err?.data?.message || "Kaydetme başarısız", "error");
    }
  };

  // Pots'u type/displayIndex ile zenginlestirip overlay uygula.
  // findSettingMeta / applyChangesToData / ChangesPanel hepsi pot.type'a
  // bakti icin her tarafta tutarli ayni dekorasyon olmali.
  const displayData = useMemo(() => {
    const decorated = decoratePots(data);
    return applyChangesToData(decorated, changes);
  }, [data, changes]);
  // Gecersiz change'leri tespit et — kaydet butonu bunlara gore disable olur
  const invalidChanges = useMemo(
    () => changes.filter((c) => !isChangeValid(data, c)),
    [changes, data],
  );
  // Her section icin pending change sayisi — FilterTabs pulsing dot'u icin
  const sectionChangeCounts = useMemo(() => {
    const map = {};
    for (const c of changes) {
      const sid = sectionOfChange(c);
      if (sid) map[sid] = (map[sid] || 0) + 1;
    }
    return map;
  }, [changes]);
  const filtered = useMemo(
    () => filterByLabel(displayData, search),
    [displayData, search],
  );

  // Ilk yukleme (cache'te veri yok) veya manuel yenile — komple skeleton
  if (isLoading || (!data && isFetching) || isManualRefresh || isSaving) {
    return (
      <SettingsSkeleton
        message={
          isSaving
            ? "Ayarlar kaydediliyor..."
            : isManualRefresh
              ? "Cihaz ayarları yenileniyor..."
              : "Cihaz ayarları yükleniyor..."
        }
      />
    );
  }
  if (error) {
    const isOffline = error?.status === 504;
    return (
      <EmptyState
        title={isOffline ? "Cihaz çevrimdışı" : "Ayarlar yüklenemedi"}
        message={
          isOffline
            ? "Cihaz şu an cevap vermiyor. Çevrimiçi olduğunda tekrar deneyin."
            : error?.data?.message || "Bilinmeyen hata"
        }
        action={{ label: "Tekrar Dene", onClick: handleRefresh }}
      />
    );
  }
  if (!filtered) {
    return (
      <EmptyState
        title="Sonuç bulunamadı"
        message={`'${search}' için eşleşen alan yok`}
      />
    );
  }

  const profile = filtered.profile || {};
  const custom = filtered.custom || {};
  const general = filtered.general || {};
  const payment = filtered.payment || {};
  const original = originalRef.current || {};

  // Her bölüm için alan sayısını hesapla
  const sectionCounts = {
    appearance: countFields(profile.appearance) + countFields(profile.timeDate),
    settings:
      countFields(profile.settings) +
      countFields(profile.security) +
      countFields(payment),
    general: countFields(general),
    pots: Array.isArray(custom.pots)
      ? custom.pots.reduce((acc, p) => acc + countFields(p), 0)
      : 0,
  };

  const sectionFilters = visibleSections.map((s) => ({
    id: s.id,
    label: s.label,
    icon: s.icon,
    count: sectionCounts[s.id],
    // Bu bolumde kaydedilmemis degisiklik varsa pulsing dot goster
    dot: (sectionChangeCounts[s.id] || 0) > 0,
  }));

  // Görünür bölüm hiç yoksa (ör. şablonda ayar anahtarı işaretli değil)
  // undefined kalır — başlıktaki kullanımlar ?. ile korunur.
  const activeSectionDef =
    visibleSections.find((s) => s.id === activeSection) || visibleSections[0];

  const renderActiveSection = () => {
    // Hiçbir ayar bölümü yetkisi yoksa sekme boş kalmasın — nedenini söyle.
    if (visibleSections.length === 0) {
      return (
        <div className="text-center py-10 px-4">
          <p className="text-sm font-SemiBold text-gray-600">
            Görüntülenecek ayar bölümü yok
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Ayarlar rol şablonunda anahtar bazlı açılır. Yöneticinizden
            şablonunuzun "Ayarlar" sekmesinden görebileceğiniz ayarları
            işaretlemesini isteyin.
          </p>
        </div>
      );
    }
    switch (activeSection) {
      case "appearance":
        return (
          <AppearanceSection
            appearance={profile.appearance}
            timeDate={profile.timeDate}
            onChange={handleChange}
            originalAppearance={original.profile?.appearance}
            originalTimeDate={original.profile?.timeDate}
          />
        );
      case "settings":
        return (
          <ProfileSettingsSection
            data={profile.settings}
            onChange={handleChange}
            originalData={original.profile?.settings}
            security={profile.security}
            originalSecurity={original.profile?.security}
            payment={payment}
            originalPayment={original.payment}
          />
        );
      case "general":
        return (
          <GeneralSection
            data={general}
            onChange={handleChange}
            originalData={original.general}
          />
        );
      case "pots":
        return (
          <CustomPotSection
            pots={custom.pots}
            onChange={handleChange}
            originalPots={original.custom?.pots}
            editScope={data?.general?.turkishCoffee?.editScope?.value}
          />
        );
      default:
        return null;
    }
  };

  const totalFields = Object.values(sectionCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="relative h-full overflow-hidden">
      {/* Sabit ust bolge — Kategoriler + tek satirlik arac cubugu.
          absolute ile relative parent'a yapisir; scroll altta ki ic
          kapsayicida calistigi icin bu blok sayfa tepesinde sabit kalir. */}
      <div
        ref={headerRef}
        // Başlık kutunun TAM genişliğini kaplar (left-0/right-0): alt kenarlık
        // satırın sonuna kadar gider. Negatif offset işe yaramıyordu — kök
        // `overflow-hidden` olduğu için dışarı taşan kısım kırpılıyordu.
        // Yatay boşluk artık dışarıdan değil, buradaki px'ten geliyor; içerik
        // de aynı px ile hizalanıyor (aşağıdaki scroll kabı).
        className="absolute top-0 left-0 right-0 bg-white z-20 px-3 sm:px-4 pt-3 pb-3 space-y-2.5 border-b border-gray-200 shadow-sm"
      >
        {/* Üst satır: kategori şeridi solda, alan sayısı + Yenile SAĞ ÜSTTE. */}
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <FilterTabs
              filters={sectionFilters}
              activeFilter={activeSection}
              onChange={setActiveSection}
              totalCount={sectionCounts[activeSection]}
              color={activeSectionDef?.color}
            />
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Sayaç rozetleri ve Yenile AYNI yükseklikte (h-9) — yan yana
                dururken biri diğerinden kısa kalmasın. */}
            <span className="hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 whitespace-nowrap">
              <FaCog className="w-3.5 h-3.5 text-indigo-500" />
              <span className="font-SemiBold text-gray-700">{totalFields}</span>
              alan
            </span>
            {count > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-700 whitespace-nowrap">
                <FaSync className="w-3.5 h-3.5" />
                <span className="font-SemiBold">{count}</span>
                değişiklik
              </span>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isFetching}
              title="Yenile"
              className="inline-flex items-center gap-2 shrink-0 h-9 px-2.5 sm:px-3 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-SemiBold shadow-sm hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 transition-colors"
            >
              <FaSync className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              <span className="hidden md:inline">Yenile</span>
            </button>
          </div>
        </div>

        {/* Arama — kategori adları da eşleşir (ör. "kazan", "log", "şifre"). */}
        <div className="max-w-md">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`${activeSectionDef?.label || "Ayarlar"} içinde ara...`}
          />
        </div>
      </div>

      {/* Aktif bölüm içeriği — scroll olan tek kisim. paddingTop'u
          header'in olculen yuksekligine esitledigimiz icin header'in altinda
          kalmiyor. */}
      <div
        className={`h-full overflow-y-auto px-3 sm:px-4 ${count > 0 || results ? "pb-[26rem] sm:pb-[28rem]" : "pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]"}`}
        style={{ paddingTop: headerH ? headerH + 20 : undefined }}
      >
        {/* Bekleyen değişiklikler paneli açıkken alt boşluk: panelin kapattığı
            alanlar yukarı kaydırılarak erişilebilir kalır. */}
        <div className="min-h-[200px]">{renderActiveSection()}</div>
      </div>

      <ChangesPanel
        changes={changes}
        isLoading={isWriting}
        results={results}
        onConfirm={handleSave}
        onClear={clear}
        onRemoveOne={remove}
        onClearResults={() => setResults(null)}
        data={displayData}
        originalData={originalRef.current || decoratePots(data)}
        invalidChanges={invalidChanges}
        isDrawer={isDrawer}
      />
    </div>
  );
}
